import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const REVIEWS = [
  { id: 1, nameKey: 'review.1.name', textKey: 'review.1.text', svcKey: 'review.1.service', rating: 5 },
  { id: 2, nameKey: 'review.2.name', textKey: 'review.2.text', svcKey: 'review.2.service', rating: 5 },
  { id: 3, nameKey: 'review.3.name', textKey: 'review.3.text', svcKey: 'review.3.service', rating: 5 },
  { id: 4, nameKey: 'review.4.name', textKey: 'review.4.text', svcKey: 'review.4.service', rating: 5 },
  { id: 5, nameKey: 'review.5.name', textKey: 'review.5.text', svcKey: 'review.5.service', rating: 5 },
];

const AVATARS = [
  'https://public.readdy.ai/ai/img_res/ce47e358bc974fe18c7996baef0149d6.jpg',
  'https://readdy.ai/api/search-image?query=friendly%20Turkish%20woman%20portrait%20casual%20modern%20clothing%20warm%20smile%20clean%20light%20background%20professional%20headshot%20approachable&width=80&height=80&seq=rv2&orientation=squarish',
  'https://readdy.ai/api/search-image?query=friendly%20young%20Turkish%20man%20portrait%20professional%20casual%20shirt%20confident%20warm%20smile%20clean%20light%20background%20high%20quality%20headshot&width=80&height=80&seq=rv3&orientation=squarish',
  'https://readdy.ai/api/search-image?query=friendly%20Turkish%20elderly%20man%20portrait%20casual%20clothing%20gentle%20smile%20clean%20simple%20background%20natural%20warm%20lighting%20trustworthy&width=80&height=80&seq=rv4&orientation=squarish',
  'https://readdy.ai/api/search-image?query=friendly%20Turkish%20middle-aged%20woman%20portrait%20neat%20casual%20appearance%20warm%20smile%20light%20background%20professional%20headshot%20quality&width=80&height=80&seq=rv5&orientation=squarish',
];

function Stars({ n }: { n: number }) {
  return (
    <span className="flex gap-0.5">
      {Array.from({ length: n }).map((_, i) => (
        <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
      ))}
    </span>
  );
}

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % REVIEWS.length), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="py-12 md:py-20 bg-blue-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-flex items-center gap-1.5 bg-white border border-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
            <i className="ri-chat-smile-3-fill text-blue-500 text-xs"></i>
            {t('reviews.badge')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('reviews.title')} <span className="text-blue-600">{t('reviews.highlight')}</span>
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-lg mx-auto">{t('reviews.subtitle')}</p>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap justify-center gap-6 sm:gap-12 mb-8">
          {[
            { v: '5.0 ★', l: t('reviews.stat.score') },
            { v: '500+', l: t('reviews.stat.clients') },
            { v: '%100', l: t('reviews.stat.satisfaction') },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <p className="text-2xl font-extrabold text-blue-600" style={{ fontFamily: 'Poppins, sans-serif' }}>{s.v}</p>
              <p className="text-gray-500 text-xs mt-0.5">{s.l}</p>
            </div>
          ))}
        </div>

        {/* Desktop: 3-column grid */}
        <div className="hidden md:grid grid-cols-3 gap-5 mb-5">
          {REVIEWS.slice(0, 3).map((r, i) => (
            <div key={r.id} className="bg-white rounded-2xl p-5 border border-blue-100 flex flex-col">
              <Stars n={r.rating} />
              <p className="text-gray-700 text-sm leading-relaxed mt-3 flex-1">"{t(r.textKey)}"</p>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                  <img src={AVATARS[i]} alt={t(r.nameKey)} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{t(r.nameKey)}</p>
                  <p className="text-blue-500 text-xs">{t(r.svcKey)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:grid grid-cols-2 gap-5 max-w-2xl mx-auto">
          {REVIEWS.slice(3).map((r, i) => (
            <div key={r.id} className="bg-white rounded-2xl p-5 border border-blue-100 flex flex-col">
              <Stars n={r.rating} />
              <p className="text-gray-700 text-sm leading-relaxed mt-3 flex-1">"{t(r.textKey)}"</p>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                  <img src={AVATARS[i + 3]} alt={t(r.nameKey)} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{t(r.nameKey)}</p>
                  <p className="text-blue-500 text-xs">{t(r.svcKey)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: carousel */}
        <div className="md:hidden">
          <div className="bg-white rounded-2xl p-5 border border-blue-100">
            <Stars n={REVIEWS[active].rating} />
            <p className="text-gray-700 text-sm leading-relaxed mt-3">"{t(REVIEWS[active].textKey)}"</p>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
              <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                <img src={AVATARS[active]} alt={t(REVIEWS[active].nameKey)} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">{t(REVIEWS[active].nameKey)}</p>
                <p className="text-blue-500 text-xs">{t(REVIEWS[active].svcKey)}</p>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-4">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`rounded-full transition-all cursor-pointer ${i === active ? 'bg-blue-600 w-5 h-2' : 'bg-blue-200 w-2 h-2'}`}
                aria-label={`Yorum ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 bg-blue-600 rounded-2xl px-6 py-5">
          <p className="text-white font-bold text-sm text-center sm:text-left">{t('reviews.cta')}</p>
          <div className="flex gap-3 shrink-0">
            <a href="tel:+905076074165" className="flex items-center gap-1.5 bg-white text-blue-700 hover:bg-blue-50 font-bold text-sm px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i> {t('nav.call')}
            </a>
            <a href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20konusunda%20yard%C4%B1m%20istiyorum." target="_blank" rel="nofollow noreferrer" className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line"></i> WA
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
